/* eslint-disable camelcase */
import {
  AccountVacanciesFiltersService,
  AccountVacanciesIntegrationsService,
  AccountVacanciesPromotionService,
  AccountVacancyService
} from '.'
import { ApiService } from '~/shared/api'
import { RequestService } from '~/services/RequestService'
import { AccountVacancyModel } from '~/model/AccountVacancies'
import { isString } from '~/shared/utils/guards/guards'
import type {
  GetAccountVacanciesParams,
  ToggleVisibilityParams,
  DeleteVacancyParams,
  AccountVacanciesResponse
} from '~/interfaces/AccountVacancies'

class AccountVacanciesService extends ApiService {
  public filtersService: AccountVacanciesFiltersService
  public integrationsService: AccountVacanciesIntegrationsService
  public promotionService: AccountVacanciesPromotionService
  public detailService: AccountVacancyService

  constructor(requestService: RequestService) {
    super(requestService)
    this.filtersService = new AccountVacanciesFiltersService(requestService)
    this.integrationsService = new AccountVacanciesIntegrationsService(requestService)
    this.promotionService = new AccountVacanciesPromotionService(requestService)
    this.detailService = new AccountVacancyService(requestService)
  }

  async getAccountVacancies(params: GetAccountVacanciesParams): Promise<AccountVacanciesResponse> {
    const search = isString(params.search) ? params.search : ''
    const sort = isString(params.sort) ? params.sort : 'position_value.keyword-asc'

    const isDeleted = params.isDeleted

    const query: Record<string, string | number | boolean> = {
      token: params.token,
      is_lk: 1,
      is_all: isDeleted ? 1 : params.includeAll ?? 0,
      search,
      sort
    }

    if (params.page) {
      query.page = params.page as number
    }
    if (params.per_page) {
      query.per_page = params.per_page as number
    }

    if (params.isPublished) {
      query.is_published = params.isPublished
    }
    if (isDeleted) {
      query.is_deleted = isDeleted
    }
    if (params.vacancySourceCode) {
      query['mf:vacancy_source_code_kw'] = params.vacancySourceCode
    }
    if (params.isVacancyPromotion) {
      query['mf:is_vacancy_promotion'] = params.isVacancyPromotion
    }
    if (params.filtersQuery) {
      Object.assign(query, params.filtersQuery)
    }

    const { data } = await this.requestService.request<{
      data?: {
        data?: unknown[]
        current_page?: number
        last_page?: number
        per_page?: number
        total?: number
      }
    }>({
      url: '/api/v1/yavagro/vacancies',
      baseURL: params.baseUrl,
      params: query
    })

    const responseData = data?.data
    const list = responseData?.data
    const vacancies = Array.isArray(list) ? list.map(item => new AccountVacancyModel(item)) : []

    return {
      list: vacancies,
      pagination: {
        currentPage: Number(responseData?.current_page ?? 1),
        lastPage: Number(responseData?.last_page ?? 1),
        perPage: Number(responseData?.per_page ?? vacancies.length),
        total: Number(responseData?.total ?? vacancies.length)
      }
    }
  }

  async toggleVisibility({ baseUrl, token, vacancyId, nextStatus }: ToggleVisibilityParams): Promise<void> {
    await this.requestService.request({
      url: `/api/v1/yavagro/vacancies/${vacancyId}/status`,
      baseURL: baseUrl,
      method: 'PUT',
      data: { token, vacancy: { status: nextStatus } }
    })
  }

  async deleteVacancy({ baseUrl, token, vacancy }: DeleteVacancyParams): Promise<void> {
    await this.requestService.request({
      url: `/api/v1/yavagro/vacancies/${vacancy.id}`,
      baseURL: baseUrl,
      method: 'POST',
      data: {
        token,
        vacancy: {
          is_deleted: true
        }
      }
    })
  }
}

export default AccountVacanciesService
